/**
 * Loads the issue-tracker status/stage vocabulary and resolves keys to the
 * SERVER'S words.
 *
 * Its own mixin rather than part of the Jira tab's controller because two
 * unrelated screens need it — the issues table and the support-ticket detail
 * sidebar — and neither should own the other's fetch. Same shape and role as
 * `common/statusClassification/syncStateCatalog.controller.js`, and filed
 * alongside it rather than inside `issueStatusPill/`: a folder named for one
 * presentational component is no place for a mixin two screens compose.
 *
 * The underlying fetch is cached for the session by the data layer and never
 * throws, so calling `loadIssueStatusMap()` on every mount is free: at worst the
 * map is null and the pills fall back to their bundled labels.
 *
 * It is still skipped without the scope. `GET /api/admin/jira/status-map` is
 * gated on `admin:jira:tickets:view` like the rest of `/api/admin/jira/*`, so
 * for a role without it the call is a guaranteed 403 on every Support and
 * ticket-detail mount — swallowed, but logged by the browser and paid for on
 * every page load, to fill a map whose only consumers are already hidden.
 */
import { SCOPES } from '../../../constants/scopes.constants';

class IssueStatusMapController {
    /**
     * Whether this session may see Jira issues at all — the Support screen's
     * Jira tab and the linked-issues card on a ticket.
     *
     * A method rather than a `hasScope` call inlined in each render, so the
     * question is asked the same way in both places and can be tested without
     * mounting either screen.
     *
     * @returns {boolean}
     */
    canViewJiraTickets() {
        return this.hasScope(SCOPES.JIRA_TICKETS_VIEW);
    }

    async loadIssueStatusMap() {
        if (!this.canViewJiraTickets()) return null;

        const map = await this.data.fetchJiraStatusMap();
        if (this._unmounted) return map;
        this.setState({ issueStatusMap: map || null });
        return map;
    }

    /**
     * A status' served entry, or undefined until the map loads — which is the
     * pill's cue to use its bundled fallback label.
     */
    servedStatusEntry(statusKey) {
        return this.state.issueStatusMap?.statuses?.find((s) => s.key === statusKey);
    }

    /**
     * The served label for a fine-grained stage. Falls back to the raw key so an
     * unknown stage is still visible rather than blank.
     */
    servedStageLabel(stageKey) {
        if (!stageKey) return null;
        return this.state.issueStatusMap?.stages?.find((s) => s.key === stageKey)?.label || stageKey;
    }
}

export default IssueStatusMapController;
